import { useEffect, useRef } from 'react';
import { Box, Button, CircularProgress, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';

import { Seo } from '../components/seo/Seo';
import { trackViewCaliber } from '../lib/analytics/events';
import { ListingCard } from '../components/search/ListingCard';
import { buildCanonicalUrl } from '../utils/seo/canonical';
import { caliberToSlug, findCaliberBySlug } from '../utils/caliber';
import {
  buildCaliberLandingDescription,
  buildCaliberLandingJsonLd,
  buildCaliberLandingTitle,
  summarizeCaliberListings,
} from '../utils/caliberLanding';
import {
  getTaxonomy,
  searchListings,
  type SearchResponse,
  type TaxonomyTerm,
} from '../services/searchApi';

const RESULT_LIMIT = 24;

function formatCpr(cpr: number | null): string {
  return cpr == null ? '—' : `$${cpr.toFixed(3)}`;
}

/**
 * Per-caliber landing page (`/ammo/:caliberSlug`, #250).
 *
 * A real, indexable page — not a redirect. It resolves the slug to a canonical
 * caliber via the taxonomy, then shows live stats (in-stock count, retailers,
 * lowest $/round) and an embedded in-stock result set, with a self-referencing
 * canonical at `/ammo/:slug` and caliber-specific JSON-LD. A slug that doesn't
 * resolve falls back to a keyword search (preserving the old #105 behavior)
 * rather than rendering a thin/duplicate page.
 */
export function CaliberLandingPage() {
  const theme = useTheme();
  const t = theme.palette.tactical;
  const { caliberSlug = '' } = useParams<{ caliberSlug: string }>();

  const taxonomyQuery = useQuery<TaxonomyTerm[]>({
    queryKey: ['taxonomy', 'calibers'],
    queryFn: () => getTaxonomy('calibers'),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const caliber = findCaliberBySlug(caliberSlug, taxonomyQuery.data ?? []);

  const listingsQuery = useQuery<SearchResponse>({
    queryKey: ['caliber-landing', caliber],
    queryFn: () =>
      searchListings({
        caliber: [caliber as string],
        inStockOnly: true,
        sort: 'pricePerRound',
        sortDir: 'asc',
        limit: RESULT_LIMIT,
      }),
    enabled: Boolean(caliber),
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  // GA4 `view_caliber` event (#256) — once per resolved caliber, surfacing
  // "popular calibers". Consent-gated inside the helper.
  const trackedCaliber = useRef<string | null>(null);
  useEffect(() => {
    if (!caliber || trackedCaliber.current === caliber) return;
    trackedCaliber.current = caliber;
    trackViewCaliber({ caliber, inStockCount: listingsQuery.data?.totalResults });
  }, [caliber, listingsQuery.data?.totalResults]);

  // Wait for the taxonomy before deciding whether the slug resolves.
  if (taxonomyQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 6, justifyContent: 'center' }}>
        <CircularProgress size={24} aria-hidden="true" />
        <Typography role="status">Loading caliber…</Typography>
      </Box>
    );
  }

  // Unresolvable slug → keyword search (old #105 fallback), never a thin page.
  if (!caliber) {
    const keywords = caliberSlug.replace(/-+/g, ' ').trim();
    return <Navigate to={`/search?q=${encodeURIComponent(keywords)}`} replace />;
  }

  const slug = caliberToSlug(caliber);
  const canonical = buildCanonicalUrl(`/ammo/${slug}`);
  const summary = summarizeCaliberListings(listingsQuery.data);
  const hits = listingsQuery.data?.results ?? [];
  const loading = listingsQuery.isLoading;

  const stats: Array<{ label: string; value: string }> = [
    { label: 'In-stock listings', value: summary.inStockCount.toLocaleString() },
    { label: 'Retailers', value: summary.retailerCount.toLocaleString() },
    { label: 'Lowest price / round', value: formatCpr(summary.lowestCpr) },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 } }}>
      <Seo
        title={buildCaliberLandingTitle(caliber)}
        description={buildCaliberLandingDescription(caliber, summary)}
        canonical={canonical}
        jsonLd={buildCaliberLandingJsonLd(caliber, canonical, hits)}
      />

      <Typography component="h1" variant="h3" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
        {caliber} Ammo
      </Typography>
      <Typography sx={{ mt: 1.5, color: t.dim, maxWidth: 720 }}>
        Compare in-stock {caliber} ammunition across many retailers, ranked by price per round, data
        freshness, and retailer trust. Prices update continuously — use the filters for bullet
        weight, bullet type, case material, and more.
      </Typography>

      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          maxWidth: 640,
          borderTop: `1px solid ${t.line}`,
          borderBottom: `1px solid ${t.line}`,
        }}
      >
        {stats.map((s, i) => (
          <Box
            key={s.label}
            sx={{
              px: 3,
              py: 3,
              borderRight: { sm: i < stats.length - 1 ? `1px solid ${t.line}` : 'none' },
            }}
          >
            <Typography component="div" sx={{ fontSize: '1.9rem', fontWeight: 600, lineHeight: 1 }}>
              {loading ? <Skeleton width={72} /> : s.value}
            </Typography>
            <Typography component="div" sx={{ fontSize: 13, color: t.dim, mt: 0.75 }}>
              {s.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
        <Button
          component={RouterLink}
          to={`/search?caliber=${encodeURIComponent(caliber)}&inStockOnly=true`}
          variant="contained"
          color="primary"
        >
          Refine {caliber} with filters
        </Button>
      </Stack>

      <Typography component="h2" variant="h6" sx={{ mt: 5, mb: 2, fontWeight: 600 }}>
        Cheapest in-stock {caliber}
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={120} />
          ))}
        </Box>
      ) : hits.length > 0 ? (
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {hits.map((h) => (
            <Box component="li" key={h.listingId}>
              <ListingCard hit={h} />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography role="status" sx={{ color: t.dim }}>
          No in-stock {caliber} listings right now. Create an alert from the search page and we’ll
          notify you when it’s back.
        </Typography>
      )}
    </Box>
  );
}

export default CaliberLandingPage;
